"use client"

import { useCallback, useState, type CSSProperties, type FormEvent } from "react"
import dynamic from "next/dynamic"
import { ArrowRight, AtSign, Check, Mail, Phone } from "lucide-react"
import { PanelBar } from "@/components/marketing/landing-v2/PanelBar"
import { useReveal } from "@/components/marketing/landing-v2/useReveal"
import {
  buildWorkflowNodes,
  WorkflowRail,
} from "@/components/marketing/landing-v2/WorkflowRail"
import {
  CONTACT_METHOD_IDS,
  validateContact,
  type ContactMethodId,
  type Landing2Stage,
  type LandingCategoryId,
} from "@/lib/landing-v2/content"
import type { Landing2Copy, Landing2Locale } from "@/lib/landing-v2/copy"

// Loaded on demand: the phone metadata is only needed once the Phone tab opens.
const PhoneField = dynamic(() => import("@/components/marketing/landing-v2/PhoneField"), {
  ssr: false,
  loading: () => <input className="l2-contact-input" disabled aria-hidden="true" />,
})

const METHOD_ICONS = { telegram: AtSign, phone: Phone, email: Mail } as const

type ContactPanelProps = {
  copy: Landing2Copy
  locale: Landing2Locale
  stage: Landing2Stage
  categoryId: LandingCategoryId
  submitted: boolean
  partial: boolean
  onSubmit: (method: ContactMethodId, value: string) => Promise<boolean>
  onReset: () => void
}

export function ContactPanel({
  copy,
  locale,
  stage,
  categoryId,
  submitted,
  partial,
  onSubmit,
  onReset,
}: ContactPanelProps) {
  const ref = useReveal<HTMLDivElement>()
  const [method, setMethod] = useState<ContactMethodId>("email")
  const [value, setValue] = useState("")
  const [phoneE164, setPhoneE164] = useState<string | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const handlePhoneChange = useCallback((display: string, e164: string | null) => {
    setValue(display)
    setPhoneE164(e164)
    setErrorKey((current) => (current && display ? null : current))
  }, [])

  const MethodIcon = METHOD_ICONS[method]
  const activeIndex = CONTACT_METHOD_IDS.indexOf(method)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (pending) return

    const validationError =
      method === "phone"
        ? !value.trim()
          ? "required"
          : phoneE164
            ? null
            : "phone"
        : validateContact(method, value)
    if (validationError) {
      setErrorKey(validationError)
      return
    }

    setErrorKey(null)
    setPending(true)
    const ok = await onSubmit(method, method === "phone" ? phoneE164! : value.trim())
    setPending(false)
    if (!ok) setErrorKey("submit")
  }

  return (
    <section className="l2-section" id="contact">
      <div className="l2-container">
        <div className="l2-panel l2-panel-narrow l2-contact" ref={ref}>
          <PanelBar
            label={copy.contact.label}
            stage={stage}
            stageNames={copy.discovery.stages}
          />

          {submitted ? (
            <div className="l2-panel-body l2-contact-success">
              <span className="l2-success-mark" aria-hidden="true">
                <Check size={17} strokeWidth={3} />
              </span>
              <span className="l2-label">{copy.contact.successLabel}</span>
              <h2 className="l2-h3">
                {partial ? copy.contact.partialTitle : copy.contact.completeTitle}
              </h2>
              <p className="l2-body">
                {partial ? copy.contact.partialBody : copy.contact.completeBody}
              </p>
              <WorkflowRail nodes={buildWorkflowNodes(copy, categoryId)} />
              <button type="button" className="l2-text-button" onClick={onReset}>
                {copy.contact.edit}
              </button>
            </div>
          ) : (
            <form className="l2-panel-body" onSubmit={handleSubmit} noValidate>
              <div className="l2-panel-head">
                <h2 className="l2-h2">{copy.contact.title}</h2>
                <p className="l2-panel-sub">{copy.contact.subtitle}</p>
              </div>

              <div
                className="l2-segmented"
                role="radiogroup"
                aria-label={copy.contact.label}
                style={{ "--active": activeIndex } as CSSProperties}
              >
                <i className="l2-segmented-thumb" aria-hidden="true" />
                {CONTACT_METHOD_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={method === id}
                    className="l2-segmented-tab"
                    onClick={() => {
                      setMethod(id)
                      setValue("")
                      setPhoneE164(null)
                      setErrorKey(null)
                    }}
                  >
                    {copy.contact.methods[id].label}
                  </button>
                ))}
              </div>

              <div className="l2-contact-field" data-invalid={errorKey ? true : undefined}>
                {method === "phone" ? (
                  <PhoneField
                    locale={locale}
                    countryLabel={copy.contact.phoneCountry}
                    inputLabel={copy.contact.methods.phone.label}
                    fallbackPlaceholder={copy.contact.methods.phone.placeholder}
                    invalid={Boolean(errorKey)}
                    onChange={handlePhoneChange}
                  />
                ) : (
                  <>
                    <span className="l2-contact-icon" aria-hidden="true">
                      <MethodIcon size={16} strokeWidth={1.9} />
                    </span>
                    <input
                      className="l2-contact-input"
                      value={value}
                      onChange={(event) => {
                        setValue(event.target.value)
                        if (errorKey) setErrorKey(null)
                      }}
                      placeholder={copy.contact.methods[method].placeholder}
                      aria-label={copy.contact.methods[method].label}
                      aria-invalid={errorKey ? true : undefined}
                      inputMode={method === "email" ? "email" : "text"}
                      autoComplete={method === "email" ? "email" : "off"}
                    />
                  </>
                )}
                <button type="submit" className="l2-button l2-button-inline" disabled={pending}>
                  {pending ? copy.contact.sending : copy.contact.submit}
                  <ArrowRight size={15} strokeWidth={2.2} />
                </button>
              </div>

              <p className="l2-contact-foot" role={errorKey ? "alert" : undefined}>
                {errorKey ? (
                  <span className="l2-error">
                    {copy.contact.errors[errorKey as keyof typeof copy.contact.errors]}
                  </span>
                ) : (
                  copy.contact.supporting
                )}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}
